import type { Ref } from "vue";

interface Position {
  x: number;
  y: number;
}

interface CanvasState {
  scale: Ref<number>;
  panPosition: Ref<Position>;
}

export function usePixelCoordinates(
  width: number,
  pixelSize: number,
  canvasState: CanvasState
) {
  // Extract mouse position calculation into a reusable function
  const getRelativeMousePosition = (
    event: MouseEvent | Touch,
    canvasElement: HTMLCanvasElement | null
  ): Position | null => {
    const rect = canvasElement?.getBoundingClientRect();
    const containerRect =
      canvasElement?.parentElement?.parentElement?.getBoundingClientRect();

    if (!rect || !containerRect) return null;

    // Use clientX/clientY which exist on both MouseEvent and Touch
    return {
      x:
        event.clientX -
        containerRect.left -
        (containerRect.width - rect.width) / 2,
      y:
        event.clientY -
        containerRect.top -
        (containerRect.height - rect.height) / 2,
    };
  };

  // Convert mouse position to canvas coordinates
  const getCanvasCoordinates = (mousePos: Position): Position => {
    return {
      x:
        (mousePos.x - canvasState.panPosition.value.x) /
        canvasState.scale.value,
      y:
        (mousePos.y - canvasState.panPosition.value.y) /
        canvasState.scale.value,
    };
  };

  // Check if coordinates are within canvas bounds
  const isWithinCanvasBounds = (x: number, y: number) => {
    return x >= 0 && x < width && y >= 0 && y < width;
  };

  // Convert mouse coordinates to pixel index
  const getPixelIndex = (
    event: MouseEvent | Touch,
    canvas: HTMLCanvasElement | null
  ): number => {
    if (!canvas) return -1;

    const mousePos = getRelativeMousePosition(event, canvas);
    if (!mousePos) return -1;

    const canvasPos = getCanvasCoordinates(mousePos);

    // Convert to pixel coordinates
    const pixelX = Math.floor(canvasPos.x / pixelSize);
    const pixelY = Math.floor(canvasPos.y / pixelSize);

    // Check bounds
    // if (!isWithinCanvasBounds(pixelX, pixelY)) {
    //   return -1;
    // }

    return pixelY * width + pixelX;
  };

  // Get mouse position in canvas coordinates
  const getMouseCanvasPosition = (
    event: MouseEvent | Touch,
    canvas: HTMLCanvasElement | null
  ): Position => {
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    return { x, y };
  };

  return {
    getRelativeMousePosition,
    getCanvasCoordinates,
    isWithinCanvasBounds,
    getPixelIndex,
    getMouseCanvasPosition,
  };
}
